import {
  FluidAlertConfig,
  FluidAlertTranslationOptions,
  FluidDataLayerComponentConfiguration,
  FluidFloatingPosition,
  FluidSeverity,
  FluidTheme,
  FluidThemeInterface,
} from '@lmig/fluid-core';
import { generateUniqueId } from '@lmig/fluid-core/lib/helpers/string.helpers';
import {
  Component,
  ComponentInterface,
  Element,
  Event,
  EventEmitter,
  h,
  Host,
  Method,
  Prop,
  State,
  Watch,
} from '@stencil/core';

import { CloseButton } from '../../common/fluid-common-button.components';
import { ALERT_DISMISSIBLE, ALERT_LOADING } from '../../constants/constants';
import { useI18n } from '../../providers/fluid-i18n-provider/adapter/fluid-i18n.adapter';
import { FluidI18nStore } from '../../providers/fluid-i18n-provider/store/fluid-i18n.store';
import { Utils } from '../../utils/utils';
import {
  FluidDataLayerUtils,
  sendEvent,
} from '../fluid-data-layer/fluid-data-layer.utils';
import { AlertBody, AlertIcon } from './components/fluid-alert.components';

/**
 * @displayName Alert
 *
 * @contextData FLUID Alerts display a concise, important message in a way that attracts the user's attention.
 * <br/><br/>
 * Alert variations have specific colors, icons, and content considerations associated with them to communicate certain types of information.
 *
 * @slot message - Named slot for custom message/content (supports HTML). Only available if "message" Prop is NOT provided.
 * @slot icon - Named slot for custom icon. When provided, replaces Alert's default "severity" Icon
 *
 * @builder true
 *
 * @analyticsAware ['alertDismissed', 'alertShown']
 *
 */
@Component({
  tag: 'fluid-alert',
  styleUrl: 'fluid-alert.scss',
  shadow: true,
})
export class FluidAlert implements ComponentInterface, FluidThemeInterface {
  // ================================================================ //
  // -- Own Properties (Private)

  /**
   * Reference to custom slotted icon element OR undefined if "icon"
   * slot not used.
   */
  private _hostedIcon: HTMLElement;

  /**
   * Reference to "timeoutID" returned from "setTimeout()" call for close
   * delay (when "timeout" Prop is provided).
   */
  private _timeoutID: number;

  // ================================================================ //
  // -- Host Element

  /**
   * Reference to the host element.
   */
  @Element() host: HTMLFluidAlertElement;

  // ================================================================ //
  // -- Internationalisation

  /* @internal */
  @Prop({ mutable: true }) fluidTranslate: FluidAlertTranslationOptions;
  private _translateId: string;
  @State() _i18nStore: FluidI18nStore | undefined;

  // ================================================================ //
  // -- States

  /**
   * Whether the Alert is visible or hidden. Separate from `visible` Prop
   * to support pure-CSS fading animation. Set to "false" after fade-out
   * completes, which removes Alert from DOM.
   */
  @State() _visible = false;

  // ================================================================ //
  // -- Props

  /**
   * What theme settings should the component use?
   */
  @Prop({ mutable: true }) theme: FluidTheme = FluidTheme.CORP;

  /**
   * If FluidThemeProvider is used, should this component override the
   * global theme with its local theme?
   */
  @Prop({ mutable: true }) overrideGlobalTheme: boolean;

  /**
   * Determines the Alert's color scheme & Icon based on its purpose.
   */
  @Prop({ mutable: true }) severity: FluidSeverity = FluidSeverity.INFO;

  /**
   * Whether the Alert is visible or hidden.
   */
  @Prop({ mutable: true, reflect: true }) visible = true;

  @Watch('visible')
  onVisibleChanged() {
    const alertState = this.getAlertState();

    this.visible
      ? this.alertShown.emit(alertState)
      : this.alertDismissed.emit(alertState);

    sendEvent(
      this.host,
      this.parentLayerId,
      this.visible ? 'alertShown' : 'alertDismissed',
      alertState
    );
  }

  /**
   * When provided, displays a title at the top of the Alert (above the
   * "message").
   */
  @Prop({ mutable: true }) alertTitle: string;

  /**
   * Message/content displayed within the Alert's body (accepts HTML).
   * If message Prop is undefined, Alert will render a "message" named slot.
   */
  @Prop({ mutable: true }) message: string;

  /**
   * When true, Alert will display a "close" button that will dismiss/
   * hide the Alert when clicked. NOTE: only available for "info" &
   * "success" severity levels (i.e. "warning" & "error" Alerts are NOT
   * dismissible per UX standards).
   */
  @Prop({ mutable: true }) dismissible: boolean;

  @Watch('dismissible')
  validateDismissible(): void {
    // Ensures that only "success" & "info" Alerts are dismissible
    if (!this.dismissible || !this.isPersistent()) return;

    this.dismissible = false;
    Utils.consoleWarn(this.host, ALERT_DISMISSIBLE);
  }

  /**
   * How long after the alert renders should it stay on screen? (ms).
   */
  @Prop({ mutable: true }) timeout: number;

  /**
   * When true, Alert will display a "loading spinner" Icon in place
   * of the Icon associated with its "severity". NOTE: only available
   * for "info" or "success" severity levels (i.e. "warning" & "error"
   * Alerts cannot display as "loading" per UX standards).
   */
  @Prop({ mutable: true }) loading: boolean;

  @Watch('loading')
  validateLoading(): void {
    // Ensures that only "success" & "info" Alerts can be loading
    if (!this.loading || !this.isPersistent()) return;

    this.loading = false;
    Utils.consoleWarn(this.host, ALERT_LOADING);
  }

  /**
   * When true, the Alert's width will be determined by its content
   * (rather than expanding to 100% width of container).
   */
  @Prop({ mutable: true }) inline: boolean;

  /**
   * When true, removes Alert's bottom margin.
   */
  @Prop({ mutable: true }) removeMargin: boolean;

  /**
   * When true, Alert will be displayed as a "floating" element (i.e.
   * appears on top of page content) in the position specified by the
   * "position" Prop. NOTE: use the "position" Prop to customize the
   * "snackbar" Alert's appearance, & use the "timeout" Prop to
   * automatically hide Alert after a specified time limit.
   */
  @Prop({ mutable: true }) snackbar = false;

  /**
   * When "snackbar" Prop is true, determines "floating" position of the
   * Alert relative to the viewport. NOTE: this Prop ONLY applies to
   * "snackbar" Alerts & will be ignored if "snackbar" Prop is false.
   */
  @Prop({ mutable: true }) position: FluidFloatingPosition =
    FluidFloatingPosition.BOTTOM_CENTER;

  /**
   * When provided, the "identifier" string will be prepended to
   * "data-test-id" attributes on Alert's inner architecture to help
   * facilitate testing.
   */
  @Prop({ mutable: true }) identifier: string;

  /**
   * Single configuration object to populate Alert (instead of using
   * individual Props).
   */
  @Prop({ mutable: true }) config: FluidAlertConfig;

  @Watch('config')
  handleConfigChange() {
    this.initializeProps();
  }

  // ================================================================ //
  // -- Events

  /**
   * Event emitted when Alert is dismissed that contains current
   * "state" of Alert (FluidAlertConfig).
   */
  @Event() alertDismissed: EventEmitter<FluidAlertConfig>;

  /**
   * Event emitted when Alert is shown that contains current state of the
   * Alert (NOTE: dispatched on first load when "visible" is true & when
   * show() is called).
   */
  @Event() alertShown: EventEmitter<FluidAlertConfig>;

  // ================================================================ //
  // -- Lifecycle Hooks

  /**
   * Called every time the component is connected to the DOM.
   * @action: initializeProps - Populates local Prop values via `config` or `alertCargo`
   * @action: setGlobalTheme - updates theme to match global theme & subscribes to changes
   */
  connectedCallback(): void {
    this.initializeProps();
    useI18n.bind(this)();
    Utils.setGlobalTheme(this);
    Utils.setZIndex(this.host);
    this.identifier = this.identifier ?? this.host.id ?? generateUniqueId();
  }

  /**
   * Called once just after the component is first connected to the DOM.
   * @action: updateVisibility - "un-hides" Alert from DOM (if "visible" is true)
   * @action: getHostedIcon - Checks for a host icon, otherwise use embedded default
   * @action: validateDismissible - Validates that "error" or "warning" Alerts aren't dismissible
   * @action: validateLoading - Validates that "error" or "warning" Alerts aren't "loading"
   * @action: initiateTimeout - Begins timeout to dismiss Alert (if one exists)
   */
  componentWillLoad(): void {
    this.updateVisibility();
    this.getHostedIcon();
    this.validateDismissible();
    this.validateLoading();
    this.initiateTimeout();

    // If Alert is visible on first load, emit "alertShown" event & send to Data Layer
    this.visible && this.onVisibleChanged();
  }

  /**
   * Called when component will be updated due to Prop or @State change.
   * @action: updateVisibility - "un-hides" Alert from DOM (if "visible" is true)
   * @action: initiateTimeout - Begins timeout to dismiss Alert (if one exists)
   */
  componentWillUpdate(): void {
    this.updateVisibility();
    this.initiateTimeout();
  }

  /**
   * Called every time the component is disconnected from the DOM.
   * @action deregisterElement - removes the element from the i18n Store registry
   */
  disconnectedCallback() {
    this._i18nStore?.deregisterElement(this._translateId);
  }

  // ================================================================ //
  // -- Public Methods

  /**
   * Dismisses the Alert (when open) & emits "alertDismissed" event
   * containing Alert's Prop values.
   */
  @Method()
  async dismiss() {
    this.visible = false;
  }

  /**
   * Displays the Alert (when hidden/closed) & emits "alertShown" event
   * containing Alert's Prop values.
   */
  @Method()
  async show() {
    this.visible = true;
    this.initiateTimeout();
  }

  // ================================================================ //
  // -- Local Methods

  /**
   * Determines whether "config" or "alertCargo" Props exist, then maps
   * the appropriate object's properties to the corresponding local Props.
   */
  initializeProps(): void {
    if (this.config) {
      Utils.selectiveMapConfigToLocal(this, this.config);
    } else if (this.alertCargo) {
      Utils.deprecationWarning(
        this.host,
        'Prop',
        'alertCargo',
        'config',
        'FluidAlertConfig'
      );
      Utils.selectiveMapConfigToLocal(this, this.alertCargo);
    }
  }

  /**
   * Bundles values of all local Props into FluidAlertConfig object.
   * @return - FluidAlertConfig object to be emitted via "alertDismissed" event
   */
  getAlertState(): FluidAlertConfig {
    return {
      severity: this.severity,
      message: this.message,
      visible: this.visible,
      timeout: this.timeout,
      dismissible: this.dismissible,
      inline: this.inline,
      removeMargin: this.removeMargin,
      theme: this.theme,
      overrideGlobalTheme: this.overrideGlobalTheme,
    };
  }

  /**
   * Allows a custom icon/image wrapped in a FLUID Icon to be substituted
   * for the default Alert icon. If a child FLUID Icon exists, the Alert
   * renders a <slot /> for the hosted icon.
   */
  getHostedIcon(): void {
    this._hostedIcon = this.host.querySelector('[slot="icon"]');
  }

  /**
   * Returns true if Alert should be persistent / high priority (i.e.
   * severity is "warning" or "error").
   */
  isPersistent(): boolean {
    return (
      this.severity === FluidSeverity.WARNING ||
      this.severity === FluidSeverity.ERROR
    );
  }

  /**
   * If "timeout" Prop is set, starts timer to hide alert.
   */
  initiateTimeout(): void {
    clearTimeout(this._timeoutID);

    if (this.visible && !!this.timeout) {
      this._timeoutID = window.setTimeout(() => this.dismiss(), this.timeout);
    }
  }

  /**
   * Updates internal "_visible" state to match "visible" Prop value
   * when Alert will be shown (i.e. removes "display: none;" styles).
   */
  updateVisibility(): void {
    if (!this.visible) return;
    this._visible = true;
  }

  /**
   * Completely removes the Alert from the DOM structure ("display:
   * none;") after the "fade-out" animation is complete.
   * @param event - animation event that triggers the method call
   * (Fail-safe to ensure that no other animation event will hide the Alert)
   */
  hideAlert(event: AnimationEvent): void {
    if (this.visible || event.animationName !== 'fadeOut') return;
    Utils.nextTick().then(() => (this._visible = false));
  }

  // ================================================================ //
  // -- Render (Main)

  render() {
    return (
      <Host
        class={{
          'fluid-alert--inline': this.inline,
          'fluid-alert--hidden': !this._visible,
        }}>
        <div
          role='alert'
          onAnimationEnd={(event) => this.hideAlert(event)}
          data-test-id={`${this.identifier}-alert-container`}
          aria-hidden={String(!this._visible)}
          class={{
            [`fluid-alert theme-${this.theme}`]: true,
            [`alert-${this.severity}`]: true,
            [`snackbar-alert ${this.position}`]: this.snackbar,
            dismissible: this.dismissible,
            'fade-in': this.visible,
            'fade-out': !this.visible,
            'no-margin': this.removeMargin,
          }}
          style={
            this.snackbar && {
              zIndex: `${this.zIndex}`,
              width: this.width ? `${this.width}px` : null,
            }
          }>
          <AlertIcon
            identifier={this.identifier}
            loading={this.loading}
            theme={this.theme}
            severity={this.severity}
            hostedIcon={this._hostedIcon}
          />
          <AlertBody
            identifier={this.identifier}
            title={this.alertTitle}
            message={this.message}
            store={this._i18nStore}
            fluidTranslate={this.fluidTranslate}
          />
          <CloseButton
            identifier={`${this.identifier}-alert`}
            class='alert-dismiss'
            render={!!this.dismissible}
            title='Dismiss Alert'
            small={true}
            scale={20}
            theme={this.theme}
            onClick={() => this.dismiss()}
          />
        </div>
      </Host>
    );
  }

  // ================================================================ //
  // -- Internal Props
  //
  // -- Note: This is not a public API - these properties
  // -- should only be used internally by FLUID components.

  /**
   * Overrides default CSS z-index value for snackbar.
   * @internal
   */
  @Prop({ mutable: true }) zIndex: number;

  /**
   * If the element should be configured at a component level for the FLUID data layer,
   * this configuration dictates how it should be instrumented
   * @internal
   * @experimental
   */
  @Prop({ mutable: true })
  analyticsConfig: FluidDataLayerComponentConfiguration;
  parentLayerId: string;
  setupAnalytics() {
    if (this.analyticsConfig) {
      this.parentLayerId = FluidDataLayerUtils.getClosestDataLayer(
        this.host
      )?.id;
    }
  }

  // ================================================================ //
  // -- Deprecated Props

  /**
   * DEPRECATED: "alertCargo" is now deprecated. Instead, use the
   * "config" Prop (same functionality, just a different naming
   * convention) -- Single configuration object to populate Alert
   * (instead of using individual Props).
   * @deprecated
   */
  @Prop({ mutable: true }) alertCargo: FluidAlertConfig;

  @Watch('alertCargo')
  handleDataUpdate() {
    this.initializeProps();
  }

  /**
   * DEPRECATED: "width" is now deprecated. "snackbar" Alerts
   * are now responsive based on viewport size & "message" length.
   * If this Prop is used, Alert will NOT be responsive --
   * When "snackbar" Prop is true, defines the Alert's width. NOTE:
   * this Prop ONLY applies to "snackbar" Alerts & will be ignored
   * if "snackbar" Prop is false.
   * @deprecated
   */
  @Prop({ mutable: true }) width: number;
}
